import { 
    collection, 
    addDoc, 
    updateDoc, 
    doc, 
    onSnapshot, 
    query, 
    where, 
    serverTimestamp, 
    getDoc 
} from 'firebase/firestore';
import { db } from '../firebase-config';

const CHALLENGES_COLLECTION = 'artifacts/whispers-of-harmony/public/data/challenges';

/**
 * Creates a new challenge invitation.
 */
export const createChallenge = async (initiator, targetId, gameType) => {
    try {
        const docRef = await addDoc(collection(db, CHALLENGES_COLLECTION), {
            initiatorId: initiator.id,
            initiatorName: initiator.displayName,
            targetId: targetId,
            gameType: gameType,
            status: 'pending',
            createdAt: serverTimestamp(),
            mode: 'survival' // Default mode for now
        });
        return { success: true, challengeId: docRef.id };
    } catch (error) {
        console.error("Error creating challenge:", error);
        return { success: false, error };
    }
};

/**
 * Responds to a challenge (accept/reject).
 */
export const respondToChallenge = async (challengeId, response) => {
    try {
        const challengeRef = doc(db, CHALLENGES_COLLECTION, challengeId);
        
        const updateData = {
            status: response, // 'accepted' or 'rejected'
            respondedAt: serverTimestamp()
        };

        if (response === 'accepted') {
            // Initialize basic game state tracking
            updateData.gameState = {
                startedAt: serverTimestamp(),
                players: {} // Will be populated as players connect
            };
        }

        await updateDoc(challengeRef, updateData);
        return { success: true };
    } catch (error) {
        console.error("Error responding to challenge:", error);
        return { success: false, error };
    }
};

/**
 * Listens for incoming challenges for the current user.
 */
export const subscribeToIncomingChallenges = (userId, callback) => {
    if (!userId) return () => {};

    const q = query(
        collection(db, CHALLENGES_COLLECTION),
        where('targetId', '==', userId),
        where('status', '==', 'pending')
    );

    return onSnapshot(q, (snapshot) => {
        const challenges = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        callback(challenges);
    });
};

/**
 * Listens to a specific active challenge (for the initiator to know when it's accepted).
 */
export const subscribeToChallengeUpdates = (challengeId, callback) => {
    if (!challengeId) return () => {};

    return onSnapshot(doc(db, CHALLENGES_COLLECTION, challengeId), (docSnap) => {
        if (docSnap.exists()) {
            callback({ id: docSnap.id, ...docSnap.data() });
        }
    });
};

/**
 * Updates the survival time/score for a player in a challenge.
 */
export const updatePlayerSurvival = async (challengeId, playerId, role, survivalTime, score, isGameOver) => {
    try {
        const challengeRef = doc(db, CHALLENGES_COLLECTION, challengeId);
        
        const updateKey = `results.${playerId}`;
        const updates = {
            [`${updateKey}.survivalTime`]: survivalTime,
            [`${updateKey}.score`]: score,
            [`${updateKey}.status`]: isGameOver ? 'finished' : 'playing'
        };

        if (isGameOver) {
            const deathField = role === 'initiator' ? 'initiatorDeathTime' : 'targetDeathTime';
            updates[deathField] = serverTimestamp();
        }

        await updateDoc(challengeRef, updates);
        return { success: true };
    } catch (error) {
        console.error("Error updating survival stats:", error);
        return { success: false, error };
    }
};

/**
 * Claims victory for a specific player.
 */
export const claimVictory = async (challengeId, winnerId) => {
    try {
        const challengeRef = doc(db, CHALLENGES_COLLECTION, challengeId);
        await updateDoc(challengeRef, {
            winnerId: winnerId,
            status: 'completed',
            completedAt: serverTimestamp()
        });
        return { success: true };
    } catch (error) {
        console.error("Error claiming victory:", error);
        return { success: false, error };
    }
};
